import { useState } from 'react';
import Button from '../ui/Button';
import fertiliserService from '../../services/fertiliser';
import { errorMessage } from '../../utils/apiResponse';
import { FERT_NUTRIENTS, FORM_LABEL } from '../../utils/fertiliser';
import type { AnalysisBasis, FertForm, FertiliserProduct, FertNutrient, NutrientMap } from '../../types/fertiliser';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * A fertiliser product: its name, form and analysis. It is kept in the
 * chemicals register as the organisation's own, so it can be bought on a
 * purchase order and held in a chemical store like any chemical.
 */
export default function FertiliserProductForm({
  product, onSaved, onCancel,
}: { product?: FertiliserProduct | null; onSaved: (p: FertiliserProduct) => void; onCancel: () => void }) {
  const [name, setName] = useState(product?.product_name ?? '');
  const [manufacturer, setManufacturer] = useState(product?.manufacturer ?? '');
  const [form, setForm] = useState<FertForm | ''>(product?.fertiliser_form ?? '');
  const [basis, setBasis] = useState<AnalysisBasis>(product?.analysis_basis ?? 'w/w');
  const [density, setDensity] = useState(product?.density_kg_per_l != null ? String(product.density_kg_per_l) : '');
  const [notes, setNotes] = useState(product?.notes ?? '');
  const [analysis, setAnalysis] = useState<Record<string, string>>(() =>
    Object.fromEntries(FERT_NUTRIENTS.map((n) => [n.key, product?.nutrient_analysis?.[n.key] != null ? String(product.nutrient_analysis[n.key]) : ''])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const liquid = form === 'liquid';

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Give the product its name.'); return; }
    const a: NutrientMap = {};
    for (const n of FERT_NUTRIENTS) {
      const v = analysis[n.key].trim();
      if (!v) continue;
      const num = Number(v);
      if (!(num >= 0 && num <= 100)) { setError(`${n.label} is a percentage, 0 to 100.`); return; }
      if (num > 0) a[n.key as FertNutrient] = num;
    }
    if (!Object.keys(a).length) { setError('Give at least one nutrient in the analysis.'); return; }
    const d = density.trim() ? Number(density) : null;
    if (d != null && !(d > 0 && d <= 5)) { setError('Density is kilograms per litre, such as 1.32.'); return; }
    setSaving(true);
    try {
      const data = {
        product_name: name.trim(), manufacturer: manufacturer.trim() || null, fertiliser_form: form || null,
        nutrient_analysis: a, analysis_basis: liquid ? basis : 'w/w' as AnalysisBasis, density_kg_per_l: liquid ? d : null,
        notes: notes.trim() || null, barcode: product?.barcode ?? null,
      };
      const res = product ? await fertiliserService.updateProduct(product.id, data) : await fertiliserService.createProduct(data);
      if (!res.success || !res.data) throw res;
      onSaved(res.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the fertiliser'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block text-sm">
          <span className="text-neutral-700">Product name</span>
          <input type="text" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Urea 46" />
        </label>
        <label className="block text-sm">
          <span className="text-neutral-700">Made by</span>
          <input type="text" className={inputCls} value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-neutral-700">Form</span>
          <select className={inputCls} value={form} onChange={(e) => setForm(e.target.value as FertForm | '')}>
            <option value="">Not said</option>
            {(Object.keys(FORM_LABEL) as FertForm[]).map((f) => <option key={f} value={f}>{FORM_LABEL[f]}</option>)}
          </select>
        </label>
        {liquid && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-neutral-700">Analysis is</span>
              <select className={inputCls} value={basis} onChange={(e) => setBasis(e.target.value as AnalysisBasis)}>
                <option value="w/w">% by weight</option>
                <option value="w/v">g per 100 mL</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-neutral-700">Density (kg/L)</span>
              <input type="text" inputMode="decimal" className={inputCls} value={density} onChange={(e) => setDensity(e.target.value)} placeholder="1.32" />
            </label>
          </div>
        )}
      </div>
      {liquid && basis === 'w/w' && !density.trim() && (
        <p className="text-xs text-amber-800">Without a density, litres of this product cannot be turned into kilograms of nutrient.</p>
      )}

      <div>
        <p className="text-sm text-neutral-700 mb-2">Analysis (% of each nutrient, as on the bag or label; elemental P and K)</p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {FERT_NUTRIENTS.map((n) => (
            <label key={n.key} className="block text-sm">
              <span className="text-neutral-600" title={n.label}>{n.key}</span>
              <input
                type="text"
                inputMode="decimal"
                aria-label={`${n.label} %`}
                className="w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm text-right"
                value={analysis[n.key]}
                onChange={(e) => setAnalysis((a) => ({ ...a, [n.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      </div>

      <label className="block text-sm">
        <span className="text-neutral-700">Notes</span>
        <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      {error && <p className="text-sm text-error-700">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" isLoading={saving} onClick={save}>{product ? 'Save fertiliser' : 'Add fertiliser'}</Button>
      </div>
    </div>
  );
}
